import { GetServerSidePropsContext, NextPage } from 'next'
import qs from 'query-string'
import { useTranslation } from 'react-i18next'
import { useEffect, useMemo, useState } from 'react'
import QueryString from 'qs'

import {
  GetServerSidePropsReturnType,
  PageProps,
} from '../../../relay/Common/PageProps'
import getConfiguration from '../../../rest/services/getConfiguration'
import { initWithLangAndLabels } from '../../../i18n'
import RestApiClient from '../../../rest/client'
import {
  Classified,
  ClassifiedEmitter,
  Status,
} from '../../../rest/types/Classified'
import useFooter from '../../../relay/Footer/useFooter'
import AdsEmitterListTemplate, {
  AdsEmitterListTemplateProps,
} from '../../../templates/AdsEmitterListTemplate'
import getByEmitterId from '../../../rest/requests/classifieds/getByEmitterId'
import { ClassifiedEmitterBannerTransformer } from '../../../relay/Transformers/ClassifiedEmitterBannerTransformer'
import { ClassifiedEmitterFiltersTransformer } from '../../../relay/Transformers/ClassifiedEmitterFiltersTransformer'
import useHeaderBis from '../../../relay/header/useHeaderBis'
import getClassifiedsWithMeta from '../../../rest/requests/classifieds/getClassifiedsWithMeta'
import { PaginatedResult } from '../../../rest/types/PaginatedResult'
import { ListDisplayType } from '../../../types/classified'
import useAdsListDisplayTypes from '../../../relay/Classified/useAdsListDisplayTypes'
import { ClassifiedListPaginationTransformer } from '../../../relay/Transformers/ClassfiedListPaginationTransformer'
import { PageType } from '../../../rest/types/Page'
import Router from '../../../routes/Router'
import routes from '../../../routes/definitions'
import { EmitterType } from '../../../rest/types/api/Classified'

type Props = PageProps & {
  emitter: ClassifiedEmitter
  classifieds: PaginatedResult<Classified>
  currentPage: number
  displayTypeParams: ListDisplayType
}

const ClassifiedsEmitter: NextPage<Props> = ({
  emitter,
  classifieds,
  configuration,
  currentPage,
  displayTypeParams,
}) => {
  const headerProps = useHeaderBis(configuration, PageType.Classifieds)
  const footerProps = useFooter(configuration)
  const { t } = useTranslation()
  const [displayType, setDisplayType] = useState<ListDisplayType>(
    ListDisplayType.Card
  )

  useEffect(() => {
    Router.push(
      Router.getRouteUrl(routes.classifiedsEmitter) +
        '?' +
        QueryString.stringify(
          {
            id: emitter?.id,
            displayType: displayType,
          },
          { arrayFormat: 'comma' }
        )
    )
  }, [displayTypeParams, displayType, emitter?.id])

  const classifiedsItems = Array.isArray(classifieds?.data)
    ? classifieds.data
    : []

  const listProps = useAdsListDisplayTypes({
    displayTypeParams,
    classifieds: classifiedsItems,
  })

  const props: AdsEmitterListTemplateProps = useMemo(() => {
    return {
      banner: ClassifiedEmitterBannerTransformer({
        t,
        emitter,
        configuration,
      }),
      filters: ClassifiedEmitterFiltersTransformer({
        t,
        classifieds: classifiedsItems,
        numberClassifieds: classifieds?.meta?.pagination?.total,
        setDisplayType,
        displayTypeParams,
      }),
      ...listProps,
      ...((classifieds?.meta?.pagination?.pageCount ?? 0) > 1 && {
        pagination: ClassifiedListPaginationTransformer({
          routeId: emitter.id,
          nbPages: classifieds?.meta?.pagination?.pageCount ?? 0,
          currentPage: currentPage,
        }),
      }),
      headerProps,
      footerProps,
    }
  }, [
    classifiedsItems,
    classifieds?.meta?.pagination?.pageCount,
    classifieds?.meta?.pagination?.total,
    configuration,
    currentPage,
    displayTypeParams,
    emitter,
    footerProps,
    headerProps,
    listProps,
    t,
  ])

  return <AdsEmitterListTemplate {...props} />
}

export default ClassifiedsEmitter

export async function getServerSideProps(
  context: GetServerSidePropsContext
): GetServerSidePropsReturnType<Props> {
  const query = context.resolvedUrl.split('?')?.[1] ?? ''
  const queryParams = qs.parse(query, { arrayFormat: 'comma' })
  const id = (context?.params?.id ?? '') as string
  const displayTypeParams: any =
    queryParams?.displayType ?? ListDisplayType.Card
  const pageString = context?.params?.page ?? ''
  const page = pageString ? pageString : 1
  const pageSize = displayTypeParams === ListDisplayType.Map ? 1000 : 12

  const [configuration, rs, rsClassifieds] = await Promise.all([
    getConfiguration(),
    await RestApiClient.call(getByEmitterId({ id })),
    await RestApiClient.call(
      getClassifiedsWithMeta({
        populate: {
          classifiedCategory: {
            fields: ['name', 'form'],
          },
          image: {
            fields: ['url', 'width', 'height', 'formats', 'alternativeText'],
          },
          classified_views: {
            fields: ['id'],
            populate: {
              user: {
                fields: ['id'],
              },
            },
          },
          user: {
            fields: ['firstName', 'lastName'],
            populate: {
              avatar: {
                fields: ['url', 'width', 'height', 'formats', 'name'],
              },
              speciality: {
                fields: ['id', 'name'],
              },
            },
          },
        },
        filters: {
          // Each classified owns its own emitter: list all classifieds of the emitter's user
          $or: [
            { emitter: { documentId: id } },
            { user: { classified_emitters: { documentId: id } } },
            // Legacy classifieds have no emitter: id is then the user documentId
            { user: { documentId: id } },
          ],
          classifiedStatus: Status.Published,
        },
        pagination: {
          page: page as number,
          pageSize: pageSize,
        },
      })
    ),
  ])
  const classifieds = rsClassifieds?.data ?? { data: [], meta: {} }
  const user = classifieds.data?.[0]?.user
  // Without emitter, the banner falls back on the user of the listed classifieds
  const emitter: ClassifiedEmitter | undefined =
    rs?.data ??
    (user && user.documentId === id
      ? { id, name: '', type: EmitterType.Doctor, user }
      : undefined)
  const currentPage = page as number

  const i18n = initWithLangAndLabels('fr', configuration.translations)

  context.res.setHeader(
    'Cache-Control',
    'public, s-maxage=10, stale-while-revalidate=59'
  )

  if (!emitter) {
    return {
      notFound: true,
      props: {
        i18n,
        configuration,
      },
    }
  }

  return {
    props: {
      emitter,
      classifieds,
      currentPage,
      configuration,
      i18n,
      displayTypeParams,
    },
  }
}
